import React, { useRef, useEffect, useState } from "react";
import clsx from "clsx";
import confetti from "canvas-confetti";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Zoom from "@material-ui/core/Zoom";

import LampIcon from "src/components/LampIcon";
import { formatCurrencyValue } from "src/utils/formatter";
import type { Reconciliation } from "src/interfaces/reconciliation.interface";
import { formatZonedDate, tryYmdStringToEndOfDayUtc } from "src/utils/timezone";

import IconWrapper from "./IconWrapper";

export interface LampProps {
  reconciliation: Reconciliation;
  position: string;
  timeZone: string;
  onClick?: () => void;
  initialAnimate?: boolean;
}
const useStyles = makeStyles({
  "@keyframes pulse": {
    "0%, 20%, 40%, 60%, 80%, 100%": {
      transform: "scale(1)",
    },
    "10%, 30%, 50%, 70%, 90%": {
      transform: "scale(0.70)",
    },
  },
  "@keyframes shake": {
    "0%": {
      transform: "rotate(-5deg)",
    },
    "4%": {
      transform: "rotate(5deg)",
    },
    "8%, 24%": {
      transform: "rotate(-8deg)",
    },
    "12%, 28%": {
      transform: "rotate(8deg)",
    },
    "16%": {
      transform: "rotate(-12deg)",
    },
    "20%": {
      transform: "rotate(12deg)",
    },
    "32%": {
      transform: "rotate(-2deg)",
    },
    "36%": {
      transform: "rotate(2deg)",
    },
    "40%, 100%": {
      transform: "rotate(0deg)",
    },
  },
  actions: {
    justifyContent: "flex-end",
  },
  icon: {
    width: "100%",
    height: "100%",
  },
  animate: {
    animation: "2s linear 0s $pulse",
  },
  tooltipTooltip: {
    animation: "2s linear 0s $shake",
  },
});

function Lamp({
  reconciliation,
  position,
  timeZone,
  onClick,
  initialAnimate = false,
}: LampProps) {
  const classes = useStyles();
  const ref = useRef<HTMLDivElement>(null);
  const open = reconciliation.open;
  const openPreviousRef = useRef(reconciliation.open);
  const [storedOpen, setStoredOpen] = useState(open);
  const [animate, setAnimate] = useState(initialAnimate);

  useEffect(() => {
    let cancel = false;
    if (open !== openPreviousRef.current) {
      if (!open && ref.current) {
        setTimeout(() => {
          if (cancel) return;
          setAnimate(true);
          setStoredOpen(false);
        }, 500);
      } else {
        setStoredOpen(open);
      }
    }
    return () => {
      openPreviousRef.current = open;
      cancel = true;
    };
  }, [open]);

  useEffect(() => {
    let cancel = false;
    if (animate) {
      const current = ref.current;
      if (current) {
        const rect = current.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;
        confetti({
          particleCount: 150,
          spread: 180,
          origin: { x: x / window.innerWidth, y: y / window.innerHeight },
        });
        setTimeout(() => {
          if (cancel) return;
          setAnimate(false);
        }, 4000);
      }
    }
    return () => {
      cancel = true;
    };
  }, [animate]);

  const displayDate =
    tryYmdStringToEndOfDayUtc(reconciliation.date, timeZone) ?? new Date(0);

  return (
    <Tooltip
      interactive
      title={
        <>
          <Typography gutterBottom={false} variant="caption" display="block">
            {position} Balance: {formatCurrencyValue(reconciliation.balance)}
          </Typography>
          <Typography gutterBottom={false} variant="caption" display="block">
            {position} Date: {formatZonedDate(displayDate, timeZone)}
          </Typography>
          <Typography gutterBottom={false} variant="caption" display="block">
            {open ? "Unreconciled" : "Reconciled"}
          </Typography>
        </>
      }
    >
      <IconWrapper ref={ref} onClick={onClick}>
        <Tooltip
          open={animate}
          title="✅ Reconciled"
          arrow
          TransitionComponent={Zoom}
          placement="top"
          classes={{
            tooltip: classes.tooltipTooltip,
          }}
        >
          <LampIcon
            variant={storedOpen ? "white" : "gold"}
            className={clsx(
              classes.icon,
              !storedOpen && animate && classes.animate
            )}
          />
        </Tooltip>
      </IconWrapper>
    </Tooltip>
  );
}

export default Lamp;
